import { v4 as uuid } from 'uuid';
import { ContentBlockType, ToggleBlock } from '../../model';
import { BlockToTiptapConverter, convertBlocksToTiptapNodes, ConverterContext } from '../blocks-to-tiptap.converter';
import { convertTiptapNodesToBlocks, TiptapToBlockConverter } from '../tiptap-to-blocks.converter';
import { parseMarkdownToInlineContent, serializeInlineContentToMarkdown } from './utils/inline-content.util';

/**
 * Converts a ToggleBlock to a Tiptap details node.
 * Recursively converts nested content blocks.
 *
 * @param block - The ToggleBlock to convert
 * @param context - Optional context to pass through (e.g., 'popover' to skip popover processing)
 * @returns A Tiptap JSON node representing the details/toggle
 */
export const convertToggleBlockToTiptap = ((block, context?: ConverterContext) => {
  return {
    type: 'details',
    attrs: {
      id: block.id,
      ...(block.custom_css && { custom_css: block.custom_css }),
      ...(block.custom_style && { custom_style: block.custom_style }),
    },
    content: [
      {
        type: 'detailsSummary',
        content: parseMarkdownToInlineContent(block.title),
      },
      {
        type: 'detailsContent',
        content: convertBlocksToTiptapNodes(block.content, context),
      },
    ],
  };
}) satisfies BlockToTiptapConverter<ToggleBlock>;

/**
 * Converts a Tiptap details node back to a ToggleBlock.
 * Recursively converts nested content.
 *
 * @param json - The Tiptap JSON node to convert
 * @param context - Optional converter context to thread through recursive calls
 * @returns A ToggleBlock
 */
export const convertTiptapToToggleBlock = ((json, context?) => {
  const attrs = json.attrs ?? {};
  const summary = json.content?.find((n) => n.type === 'detailsSummary');
  const detailsContent = json.content?.find((n) => n.type === 'detailsContent');

  const title = summary?.content ? serializeInlineContentToMarkdown(summary.content) : '';

  return {
    id: (attrs['id'] as string) ?? uuid(),
    type: ContentBlockType.Toggle,
    title,
    ...(attrs['custom_css'] && { custom_css: attrs['custom_css'] as string }),
    ...(attrs['custom_style'] && { custom_style: attrs['custom_style'] as Record<string, string> }),
    content: detailsContent?.content?.length ? convertTiptapNodesToBlocks(detailsContent.content, context) : [],
  };
}) satisfies TiptapToBlockConverter;
